import * as chameleon from '@chamaeleonidae/chmln';
import { Logger } from './logging/logger';

const logger = Logger.create('chameleon');

interface ChameleonUser {
  email: string;
  firstName: string;
  lastName: string;
  edgeEnrolled: boolean;
  edgeEnrollmentDate: string;
  solutionOrgName: string;
  environment: string;
  onboardingFlowTestGroup?: string;
  company?: {
    uid: string;
  };
  [key: string]:
  | string
  | number
  | boolean
  | Date
  | null
  | undefined
  | object;
}

interface ChameleonConfig {
  fastUrl?: string;
}

type ChameleonProperties = Record<string, string | number | boolean | Date | null | object>;

function removeUndefinedValues(properties: Record<string, unknown>): ChameleonProperties {
  return Object.entries(properties).reduce((cleanedProperties, [key, value]) => {
    if (value === undefined) {
      return cleanedProperties;
    }

    if (
      value &&
      typeof value === 'object' &&
      !(value instanceof Date) &&
      !Array.isArray(value)
    ) {
      const cleanedObject = removeUndefinedValues(value as Record<string, unknown>);
      if (Object.keys(cleanedObject).length === 0) {
        return cleanedProperties;
      }

      return {
        ...cleanedProperties,
        [key]: cleanedObject,
      };
    }

    return {
      ...cleanedProperties,
      [key]: value,
    };
  }, {} as ChameleonProperties);
}

// Chameleon's client skips the whole profile update (no request is sent, though the
// attributes are still merged locally) when identify() carries a `company` that matches the
// profile's current company, even if other attributes changed. AuthenticatedRoute re-runs
// identify() with the same company on every state change, so e.g. edgeEnrolled flipping to
// true after enrollment was never posted until a page refresh. To avoid that, only send
// `company` when it differs from the last one sent for this user.
let lastIdentified: { userId: string; companyUid?: string } | null = null;

export const ReactChameleon = {
  initialize() {
    const apiKey = process.env.REACT_APP_CHAMELEON_API_KEY;
    const fastUrl = process.env.REACT_APP_CHAMELEON_FAST_URL || 'https://fast.chameleon.io/';

    if (!apiKey) {
      logger.debug(Logger.format('Chameleon API key not found, skipping initialization'));
      return;
    }

    try {
      logger.debug(Logger.format('Initializing Chameleon with API key'));

      const config: ChameleonConfig = { fastUrl };
      chameleon.init(apiKey, config);

      logger.debug(Logger.format('Chameleon initialized successfully'));
    } catch (e) {
      logger.error(Logger.format('Error initializing Chameleon', e));
    }
  },

  /**
   * If user input is null, that means the properties were sent from another place/system
   * and here in the frontend, we just need to identify the user to show targeted tours/flows
   * For example: Service Nation native users.
   * @param userId
   * @param user
   * @returns
   */
  identify(userId: string, user?: ChameleonUser) {
    try {
      const sameUser = lastIdentified?.userId === userId;
      const lastCompanyUid = sameUser ? lastIdentified?.companyUid : undefined;
      const companyUid = user?.company?.uid;
      const companyAlreadySent = !!companyUid && companyUid === lastCompanyUid;

      const properties: Record<string, unknown> = { ...user };
      if (companyAlreadySent) {
        delete properties.company;
      }

      logger.debug(Logger.format('Identifying user in Chameleon', userId));
      chameleon.identify(userId, removeUndefinedValues(properties));
      lastIdentified = { userId, companyUid: companyUid || lastCompanyUid };
    } catch (e) {
      logger.error(Logger.format('Error identifying user in Chameleon', e));
    }
  },

  clear() {
    try {
      logger.debug(Logger.format('Clearing Chameleon user'));
      lastIdentified = null;
      chameleon.clear();
    } catch (e) {
      logger.error(Logger.format('Error clearing user in Chameleon', e));
    }
  },

  show(tourId: string) {
    try {
      logger.debug(Logger.format('Showing Chameleon tour', tourId));
      chameleon.show(tourId);
    } catch (e) {
      logger.error(Logger.format('Error showing Chameleon tour', e));
    }
  },
};
